import {
    ɵComponentType as ComponentType,
    ɵDirectiveType as DirectiveType,
    ɵNG_COMP_DEF as NG_COMP_DEF,
    ɵNG_DIR_DEF as NG_DIR_DEF,
    InjectableType,
    ElementRef,
    Renderer2,
    TypeDecorator
} from '@angular/core';
import { TeamNames } from 'src/app/shared/enums/team-names.enum';

export const RENDERER_PROPS = [
    'createElement',
    'createComment',
    'createText',
    'destroyNode',
    'appendChild',
    'insertBefore',
    'removeChild',
    'selectRootElement',
    'parentNode',
    'nextSibling',
    'setAttribute',
    'removeAttribute',
    'addClass',
    'removeClass',
    'setStyle',
    'removeStyle',
    'setProperty',
    'setValue',
    'listen'
];
// This Record is used to pair component class names with their respective owners.
export const OWNED_OBJECTS: Record<string, TeamNames | TeamNames[]> = {};

/**
 * A Decorator for marking ownership of a component or directive.
 * When added to the component header, will append a team name to the error that is logged.
 * If Renderer2 and ElementRef are injected, will also add the nova-owner attribute with a value of ownerName.
 * @param ownerName The team name that owns the component or directive in question
 * @returns
 */
export function Owner(ownerName: TeamNames | TeamNames[]): TypeDecorator {
    return (type: any) => {
        decorateClass(type, ownerName);
    };
}

/**
 * Performs the appropriate decorations on the target given its type (pipe or component/directive)
 * @param type
 * @param ownerName
 * @returns
 */
function decorateClass<T>(
    type: DirectiveType<T> | ComponentType<T> | InjectableType<T>,
    ownerName: TeamNames | TeamNames[]
): void {
    registerClassWithOwnedComponents(type.name, ownerName);
    if (!isComponent(type) && !isDirective(type)) {
        return;
    }
    if (!type.prototype.ngOnInit) {
        //TODO: Figure out a way to inject Renderer2 and ElementRef from the decorator and set those values in the component
        // i.e. componentInstance.NOVA_OWNER_RENDERER = Inject(Renderer2)
        // This will result in the renderer property being picked up in decorateNgOnInit.

        // The function below will create the onInit function, but it cannot inject the renderer and element ref
        // Because of this, in order to get the owner tags in html, we must inject them manually wherever this decorator is used.
        // Previous attempts to achieve this by  overriding the constructor have not proven successful
        createOnInit(type);
    }
    type.prototype.ngOnInit = decorateNgOnInit(type.prototype.ngOnInit, ownerName, type.name);
}

// Component type guard using Angular internal properties
export function isComponent<T>(target: any): target is ComponentType<T> {
    return !!target[NG_COMP_DEF] || target.prototype.NOVA_UNIT_TEST_OWNER_FLAG;
}

// Directive type guard using Angular internal properties
export function isDirective<T>(target: any): target is DirectiveType<T> {
    return !!target[NG_DIR_DEF];
}
export function createOnInit(type: any): void {
    type.prototype.ngOnInit = () => {};
}

export function registerClassWithOwnedComponents(typeName: string, ownerName: TeamNames | TeamNames[]) {
    OWNED_OBJECTS[typeName] = ownerName;
}

export function decorateNgOnInit(
    ngOnInit: (() => void) | null | undefined,
    ownerName: TeamNames | TeamNames[],
    typeName: string
) {
    return function (this: any) {
        const componentName = typeName;

        // Invoke the original `ngOnInit` if it exists
        ngOnInit && ngOnInit.call(this);

        const props = Object.keys(this);

        const rendererProp = props.find((p) => isRenderer(this, p));
        const elementRefProp = props.find((i) => this[i] instanceof ElementRef);
        if (!elementRefProp || !rendererProp) {
            console.warn(
                `${componentName} uses the @Owner decorator. 
                To set html attributes, inject ElementRef and Renderer2 via constructor.`
            );
            return;
        }

        // Get the objects used to append the owner attribute to the HTML element
        const elementRef: ElementRef = this[elementRefProp];
        const renderer: Renderer2 = this[rendererProp];

        // Modify the element after the view initializes
        if (Array.isArray(ownerName)) {
            renderer.setAttribute(elementRef.nativeElement, 'owned-by', ownerName.join(', '));
        } else {
            renderer.setAttribute(elementRef.nativeElement, 'owned-by', ownerName);
        }
    };
}

/**
 * Checks to see if the provided property of the given object is an instance of Renderer2
   Renderer2 is injected as an instance of BaseAnimationRenderer, even though the Renderer2 type is provided in the constructor
   This check is necessary because BaseAnimationRenderer is not accessible
 * @param obj the object to look at
 * @param prop the property 
 * @returns
 */
function isRenderer(component: any, property: string): boolean {
    const missingRendererProperty = RENDERER_PROPS.find((i) => !component[property] || !component[property][i]);
    return !missingRendererProperty;
}
